import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { CompanyProvider, useCompany } from "@/hooks/useCompany";
import { currentUserHasAnyRole, isCurrentUserAdmin } from "@/lib/adminUsers";
import { usePackageAccess } from "@/lib/packageAccess";
import AppLayout from "@/components/AppLayout";
import PublicLayout from "@/components/public/PublicLayout";
import Dashboard from "@/pages/Dashboard";
import WebsitesPage from "@/pages/WebsitesPage";
import ContentFactoryPage from "@/pages/ContentFactoryPage";
import SourcesPage from "@/pages/SourcesPage";
import PublishedArticlesPage from "@/pages/PublishedArticlesPage";
import AIStudioPage from "@/pages/AIStudioPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import MediaLibraryPage from "@/pages/MediaLibraryPage";
import MediaAnalyticsPage from "@/pages/MediaAnalyticsPage";
import CreateWebsiteWizard from "@/pages/CreateWebsiteWizard";
import ImportWebsiteWizard from "@/pages/ImportWebsiteWizard";
import OnboardingWizard from "@/pages/OnboardingWizard";
import SEOCenterPage from "@/pages/SEOCenterPage";
import SEOMonitorPage from "@/pages/SEOMonitorPage";
import TranslationCenterPage from "@/pages/TranslationCenterPage";
import TemplatesPage from "@/pages/TemplatesPage";
import TemplateDetailPage from "@/pages/templates/TemplateDetailPage";
import TemplatePreviewPage from "@/pages/templates/TemplatePreviewPage";
import TemplateFormPage from "@/pages/templates/TemplateFormPage";
import SettingsPage from "@/pages/SettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import DeveloperPage from "@/pages/DeveloperPage";
import TeamsPage from "@/pages/TeamsPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CompanyDetailsPage from "@/pages/CompanyDetailsPage";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import InfoPage from "@/pages/InfoPage";
import ArticlesPage from "@/pages/ArticlesPage";
import ArticleEditorPage from "@/pages/ArticleEditorPage";
import WebsiteDetailPage from "@/pages/WebsiteDetailPage";
import PlansPage from "@/pages/PlansPage";
import AutoPublishPage from "@/pages/AutoPublishPage";
import AuthorsPage from "@/pages/AuthorsPage";
import AuthorArticlesPage from "@/pages/AuthorArticlesPage";
import BillingPlansPage from "@/pages/billing/BillingPlansPage";
import BillingSubscriptionPage from "@/pages/billing/BillingSubscriptionPage";
import BillingInvoicesPage from "@/pages/billing/BillingInvoicesPage";
import BillingCreditsPage from "@/pages/billing/BillingCreditsPage";
import SiteAiSettingsPage from "@/pages/websites/SiteAiSettingsPage";
import SiteRunsPage from "@/pages/websites/SiteRunsPage";
import OrgAiDefaultsPage from "@/pages/org/OrgAiDefaultsPage";
import AdminBillingPage from "@/pages/admin/AdminBillingPage";
import AdminPackagesPage from "@/pages/admin/AdminPackagesPage";
import AdminCatalogPage from "@/pages/admin/AdminCatalogPage";
import AdminModelsPage from "@/pages/admin/AdminModelsPage";
import AdminSubscribersPage from "@/pages/admin/AdminSubscribersPage";
import AdminInvoicesPage from "@/pages/admin/AdminInvoicesPage";
import AdminEmailTemplatesPage from "@/pages/admin/AdminEmailTemplatesPage";
import AdminInvoiceTemplatesPage from "@/pages/admin/AdminInvoiceTemplatesPage";
import AdminEmailLogsPage from "@/pages/admin/AdminEmailLogsPage";
import AdminRolesPermissionsPage from "@/pages/admin/AdminRolesPermissionsPage";
import AdminAuditLogsPage from "@/pages/admin/AdminAuditLogsPage";
import AdminServicesPage from "@/pages/admin/AdminServicesPage";
import AdminQaReviewPage from "@/pages/admin/AdminQaReviewPage";
import AdminSystemPage from "@/pages/admin/AdminSystemPage";
import CheckoutPackagePage from "@/pages/checkout/CheckoutPackagePage";
import CheckoutReviewPage from "@/pages/checkout/CheckoutReviewPage";
import CheckoutResultPage from "@/pages/checkout/CheckoutResultPage";
import UserFormPage from "@/pages/team/UserFormPage";
import UserDetailPage from "@/pages/team/UserDetailPage";
import HomePage from "@/pages/public/HomePage";
import AboutPage from "@/pages/public/AboutPage";
import ContactPage from "@/pages/public/ContactPage";
import PrivacyPolicy from "@/pages/public/PrivacyPolicy";
import LegalNotice from "@/pages/public/LegalNotice";
import Cgv from "@/pages/public/Cgv";
import Trust from "@/pages/public/Trust";
import SecurityPage from "@/pages/public/SecurityPage";
import CookiesPage from "@/pages/public/CookiesPage";
import FeaturesPage from "@/pages/public/FeaturesPage";
import HowItWorksPage from "@/pages/public/HowItWorksPage";
import ShowcasePage from "@/pages/public/ShowcasePage";
import ResourcesHubPage from "@/pages/public/ResourcesHubPage";
import ResourcesCategoryPage from "@/pages/public/ResourcesCategoryPage";
import ResourceArticlePage from "@/pages/public/ResourceArticlePage";
import { isResourceCategorySlug } from "@/lib/marketing-categories";
import SoloPublishersPage from "@/pages/public/segments/SoloPublishersPage";
import AgencyPage from "@/pages/public/segments/AgencyPage";
import MediaGroupsPage from "@/pages/public/segments/MediaGroupsPage";
import PricingPage from "@/pages/public/marketing/PricingPage";
import PricingComparisonPage from "@/pages/public/marketing/PricingComparisonPage";
import PlanFinderPage from "@/pages/public/marketing/PlanFinderPage";
import FaqPage from "@/pages/public/marketing/FaqPage";
import VsChatGPTPage from "@/pages/public/comparisons/VsChatGPTPage";
import VsAutomationToolsPage from "@/pages/public/comparisons/VsAutomationToolsPage";
import VsContentAgencyPage from "@/pages/public/comparisons/VsContentAgencyPage";
import KitchenSink from "@/pages/_dev/KitchenSink";
import { ConsentProvider } from "@/components/consent/ConsentProvider";
import CookieBanner from "@/components/consent/CookieBanner";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function AuthRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/dpadmin", search: location.search, hash: location.hash }} replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { isPlatformOwner, isLoading: companyLoading } = useCompany();
  const { data: isAdmin = false, isLoading } = useQuery({
    queryKey: ["admin-route", user?.id],
    queryFn: () => isCurrentUserAdmin(user?.id),
    enabled: !!user,
  });

  if (isLoading || companyLoading) {
    return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Checking admin access...</div>;
  }

  if (!isAdmin && !isPlatformOwner) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-md border border-border bg-card p-6 text-center">
          <h1 className="text-lg font-semibold">Access restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">Admin-only feature: this area requires a platform admin role.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

const admin = (children: React.ReactNode) => <AdminRoute>{children}</AdminRoute>;

function RoleRoute({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { user } = useAuth();
  const { data: allowed = false, isLoading } = useQuery({
    queryKey: ["role-route", user?.id, roles.join(",")],
    queryFn: () => currentUserHasAnyRole(user?.id, roles),
    enabled: !!user,
  });
  if (isLoading) return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Checking role access...</div>;
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const roles = (allowedRoles: string[], children: React.ReactNode) => <RoleRoute roles={allowedRoles}>{children}</RoleRoute>;

function PackageRoute({ feature, children }: { feature: string; children: React.ReactNode }) {
  const access = usePackageAccess();
  if (access.isLoading) {
    return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Checking package access...</div>;
  }
  if (!access.can(feature)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-md border border-border bg-card p-6 text-center">
          <h1 className="text-lg font-semibold">Package limit</h1>
          <p className="mt-2 text-sm text-muted-foreground">{access.reason(feature) ?? "Your company package does not include this feature."}</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

const gated = (feature: string, children: React.ReactNode) => <PackageRoute feature={feature}>{children}</PackageRoute>;

function CompanyAdminRoute({ children }: { children: React.ReactNode }) {
  const { role, isPlatformOwner, isLoading } = useCompany();
  if (isLoading) return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Checking company access...</div>;
  if (!isPlatformOwner && !["owner", "admin"].includes(role ?? "")) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-md border border-border bg-card p-6 text-center">
          <h1 className="text-lg font-semibold">Role required</h1>
          <p className="mt-2 text-sm text-muted-foreground">This company feature requires owner or admin access in the current workspace.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

const companyAdmin = (children: React.ReactNode) => <CompanyAdminRoute>{children}</CompanyAdminRoute>;

// /ressources/:slugOrCategory dispatches to a category page if the URL
// segment matches a known category slug, otherwise to a single-article page.
function ResourcesParamDispatcher() {
  const { slugOrCategory } = useParams<{ slugOrCategory: string }>();
  if (slugOrCategory && isResourceCategorySlug(slugOrCategory)) {
    return <ResourcesCategoryPage />;
  }
  return <ResourceArticlePage />;
}

function PlatformOwnerRoute({ children }: { children: React.ReactNode }) {
  const { isPlatformOwner, isLoading } = useCompany();
  if (isLoading) return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Checking company access...</div>;
  if (!isPlatformOwner) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const platformOwner = (children: React.ReactNode) => <PlatformOwnerRoute>{children}</PlatformOwnerRoute>;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ConsentProvider>
      <AuthProvider>
        <CompanyProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <CookieBanner />
          <Routes>
            {/* Public reader site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/about" element={<Navigate to="/a-propos" replace />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/pricing" element={<Navigate to="/tarifs" replace />} />
              <Route path="/politique-rgpd" element={<PrivacyPolicy />} />
              <Route path="/privacy" element={<Navigate to="/politique-rgpd" replace />} />
              <Route path="/mentions-legales" element={<LegalNotice />} />
              <Route path="/legal-notice" element={<Navigate to="/mentions-legales" replace />} />
              <Route path="/cgv" element={<Cgv />} />
              <Route path="/terms" element={<Navigate to="/cgv" replace />} />
              <Route path="/cgu" element={<Navigate to="/cgv" replace />} />
              <Route path="/securite" element={<SecurityPage />} />
              <Route path="/security" element={<Navigate to="/securite" replace />} />
              <Route path="/trust" element={<Trust />} />
              <Route path="/sous-traitants" element={<Navigate to="/trust" replace />} />
              <Route path="/cookies" element={<CookiesPage />} />

              {/* Marketing destinations. /tarifs renders the marketing
                  PricingPage for logged-out visitors; PricingPage itself
                  redirects authenticated users to /pricing (product-side
                  BillingPlansPage). /pricing stays untouched for in-app
                  flows. */}
              <Route path="/fonctionnalites" element={<FeaturesPage />} />
              <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
              <Route path="/tarifs" element={<PricingPage />} />
              <Route path="/tarifs/comparatif" element={<PricingComparisonPage />} />
              <Route path="/tarifs/calculateur" element={<PlanFinderPage />} />
              <Route path="/tarifs/calculator" element={<Navigate to="/tarifs/calculateur" replace />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/vitrine" element={<ShowcasePage />} />
              <Route path="/ressources" element={<ResourcesHubPage />} />
              <Route
                path="/ressources/:slugOrCategory"
                element={<ResourcesParamDispatcher />}
              />
              <Route path="/pour/editeurs-solo" element={<SoloPublishersPage />} />
              <Route path="/pour/agences-medias" element={<AgencyPage />} />
              <Route path="/pour/groupes-presse" element={<MediaGroupsPage />} />
              <Route path="/comparatif/chatgpt-seul" element={<VsChatGPTPage />} />
              <Route path="/comparatif/zapier-make" element={<VsAutomationToolsPage />} />
              <Route path="/comparatif/agence-redaction" element={<VsContentAgencyPage />} />
              {import.meta.env.DEV && (
                <Route path="/_kitchen-sink" element={<KitchenSink />} />
              )}
            </Route>

            {/* Admin / Editorial backoffice. The login lives at /dpadmin —
                /auth stays as a redirect so older marketing CTAs and any
                external bookmarks still resolve. */}
            <Route path="/dpadmin" element={<AuthPage />} />
            <Route path="/auth" element={<AuthRedirect />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/websites" element={gated("live_publishing", <WebsitesPage />)} />
              <Route path="/websites/create" element={gated("live_publishing", <CreateWebsiteWizard />)} />
              <Route path="/websites/import" element={gated("live_publishing", <ImportWebsiteWizard />)} />
              <Route path="/websites/:id" element={gated("live_publishing", <WebsiteDetailPage />)} />
              <Route path="/onboarding/:site_id" element={<OnboardingWizard />} />
              <Route path="/content-factory" element={gated("ai_article_generation", <ContentFactoryPage />)} />
              <Route path="/articles" element={gated("live_publishing", <ArticlesPage />)} />
              <Route path="/articles/:id" element={gated("live_publishing", <ArticleEditorPage />)} />
              <Route path="/authors" element={<AuthorsPage />} />
              <Route path="/authors/:id/articles" element={<AuthorArticlesPage />} />
              <Route path="/sources" element={<SourcesPage />} />
              <Route path="/ai-studio" element={gated("ai_article_generation", <AIStudioPage />)} />
              <Route path="/media-library" element={gated("media_library", <MediaLibraryPage />)} />
              <Route path="/media-library/analytics" element={gated("media_library", <MediaAnalyticsPage />)} />
              <Route path="/published" element={gated("live_publishing", <PublishedArticlesPage />)} />
              <Route path="/seo" element={gated("seo", <SEOCenterPage />)} />
              <Route path="/seo/monitor" element={gated("seo", <SEOMonitorPage />)} />
              <Route path="/translations" element={gated("translation", <TranslationCenterPage />)} />
              <Route path="/templates" element={gated("templates", <TemplatesPage />)} />
              <Route path="/templates/create" element={gated("templates", <TemplateFormPage mode="create" />)} />
              <Route path="/templates/:id" element={gated("templates", <TemplateDetailPage />)} />
              <Route path="/templates/:id/edit" element={gated("templates", <TemplateFormPage mode="edit" />)} />
              <Route path="/templates/:id/preview" element={gated("templates", <TemplatePreviewPage />)} />
              <Route path="/info" element={<InfoPage />} />
              <Route path="/websites/create/info" element={<InfoPage />} />
              <Route path="/checkout/package/:packageId" element={<CheckoutPackagePage />} />
              <Route path="/checkout/review" element={<CheckoutReviewPage />} />
              <Route path="/checkout/success" element={<CheckoutResultPage status="success" />} />
              <Route path="/checkout/cancel" element={<CheckoutResultPage status="cancel" />} />
              <Route path="/integrations" element={gated("api_embed", companyAdmin(<IntegrationsPage />))} />
              <Route path="/developpeurs" element={companyAdmin(<DeveloperPage />)} />
              <Route path="/companies" element={platformOwner(<CompaniesPage />)} />
              <Route path="/companies/:id" element={platformOwner(<CompanyDetailsPage />)} />
              <Route path="/analytics" element={<PlaceholderPage title="Analytics" description="Performance metrics & insights" />} />
              <Route path="/domains" element={gated("custom_domain", <PlaceholderPage title="Domains & Hosting" description="Domain management & deployment" />)} />
              <Route path="/team" element={gated("team_members", <TeamsPage />)} />
              <Route path="/team/users/create" element={gated("team_members", <UserFormPage mode="create" />)} />
              <Route path="/team/users/:id" element={gated("team_members", <UserDetailPage />)} />
              <Route path="/team/users/:id/edit" element={gated("team_members", <UserFormPage mode="edit" />)} />
              <Route path="/teams" element={gated("team_members", <TeamsPage />)} />
              <Route path="/billing" element={<PlansPage />} />
              <Route path="/billing/plans" element={<BillingPlansPage />} />
              <Route path="/billing/subscription" element={<BillingSubscriptionPage />} />
              <Route path="/billing/invoices" element={<BillingInvoicesPage />} />
              <Route path="/billing/credits" element={<BillingCreditsPage />} />
              <Route path="/websites/:id/settings/ai" element={gated("live_publishing", <SiteAiSettingsPage />)} />
              <Route path="/websites/:id/runs" element={gated("live_publishing", <SiteRunsPage />)} />
              <Route path="/org/settings/ai-defaults" element={<OrgAiDefaultsPage />} />
              <Route path="/automation" element={gated("automation", <AutoPublishPage />)} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin" element={admin(<AdminBillingPage />)} />
              <Route path="/admin/billing" element={admin(<AdminBillingPage />)} />
              <Route path="/admin/packages" element={admin(<AdminCatalogPage />)} />
              <Route path="/admin/packages/legacy" element={admin(<AdminPackagesPage />)} />
              <Route path="/admin/packages/create" element={admin(<AdminPackagesPage />)} />
              <Route path="/admin/packages/:id" element={admin(<AdminPackagesPage />)} />
              <Route path="/admin/packages/:id/edit" element={admin(<AdminPackagesPage />)} />
              <Route path="/admin/models" element={admin(<AdminModelsPage />)} />
              <Route path="/admin/subscribers" element={roles(["admin", "support"], <AdminSubscribersPage />)} />
              <Route path="/admin/subscriptions" element={roles(["admin", "support"], <AdminSubscribersPage />)} />
              <Route path="/admin/services" element={admin(<AdminServicesPage />)} />
              <Route path="/admin/subscribers/:id" element={roles(["admin", "support"], <AdminSubscribersPage />)} />
              <Route path="/admin/invoices" element={roles(["admin", "support"], <AdminInvoicesPage />)} />
              <Route path="/admin/invoice-templates" element={companyAdmin(<AdminInvoiceTemplatesPage />)} />
              <Route path="/admin/email-templates" element={companyAdmin(<AdminEmailTemplatesPage />)} />
              <Route path="/admin/email-logs" element={admin(<AdminEmailLogsPage />)} />
              <Route path="/admin/roles-permissions" element={admin(<AdminRolesPermissionsPage />)} />
              <Route path="/admin/audit-logs" element={admin(<AdminAuditLogsPage />)} />
              <Route path="/admin/qa-review" element={admin(<AdminQaReviewPage />)} />
              <Route path="/admin/system-review" element={admin(<AdminQaReviewPage />)} />
              <Route path="/admin/system" element={admin(<AdminSystemPage />)} />
              <Route path="/admin/autopublish" element={admin(<AutoPublishPage />)} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </TooltipProvider>
        </CompanyProvider>
      </AuthProvider>
    </ConsentProvider>
  </QueryClientProvider>
);

export default App;
